import { z } from "zod";

import { OpaqueIdSchema } from "./identity-schemas";

// ADR-0089: the support desk contract shared by the operations console, the Operations MCP, and
// the customer portal. Values mirror the CHECK constraints in `0046_support_desk.sql`.

export const SUPPORT_TICKET_STATUSES = [
  "new",
  "open",
  "waiting_on_customer",
  "on_hold",
  "solved",
] as const;
export const SupportTicketStatusSchema = z.enum(SUPPORT_TICKET_STATUSES);
export type SupportTicketStatus = z.infer<typeof SupportTicketStatusSchema>;

export const SUPPORT_TICKET_PRIORITIES = ["urgent", "high", "normal", "low"] as const;
export const SupportTicketPrioritySchema = z.enum(SUPPORT_TICKET_PRIORITIES);
export type SupportTicketPriority = z.infer<typeof SupportTicketPrioritySchema>;

export const SUPPORT_TICKET_TYPES = [
  "bug",
  "question",
  "change_request",
  "access",
  "other",
] as const;
export const SupportTicketTypeSchema = z.enum(SUPPORT_TICKET_TYPES);
export type SupportTicketType = z.infer<typeof SupportTicketTypeSchema>;

export const SupportChannelSchema = z.enum(["email", "portal", "tool", "applet", "console", "mcp"]);
export type SupportChannel = z.infer<typeof SupportChannelSchema>;

export const SupportActorKindSchema = z.enum(["customer", "employee", "workflow", "system"]);
export type SupportActorKind = z.infer<typeof SupportActorKindSchema>;

export const SupportMessageKindSchema = z.enum(["customer", "reply", "note"]);
export type SupportMessageKind = z.infer<typeof SupportMessageKindSchema>;

export const SupportDeliveryStatusSchema = z.enum(["pending", "sent", "failed", "not_sent"]);

export const SUPPORT_VIEW_KEYS = [
  "mine",
  "unassigned",
  "open",
  "waiting_on_customer",
  "on_hold",
  "solved",
] as const;
export const SupportViewKeySchema = z.enum(SUPPORT_VIEW_KEYS);
export type SupportViewKey = z.infer<typeof SupportViewKeySchema>;

export const SupportTagSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(1)
  .max(40)
  .regex(/^[a-z0-9][a-z0-9._-]*$/, "Use letters, numbers, dots, dashes, or underscores.");

export const SupportEmailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(3)
  .max(320)
  .refine((value) => z.email().safeParse(value).success, "Enter a valid email address.");

const NameSchema = z.string().trim().max(160);
const BodySchema = z.string().trim().min(1, "Write something first.").max(100_000);
const TimestampSchema = z.number().int().nonnegative();

/** The customer-visible reference. Standalone "DAS" is never a prefix (brand rule). */
export function supportReference(number: number): string {
  return `#${number}`;
}

export const SupportAssigneeSchema = z.strictObject({
  employeeId: OpaqueIdSchema,
  displayName: z.string().min(1).max(160),
});

export const SupportRequesterSchema = z.strictObject({
  name: z.string().max(160),
  email: z.string().max(320).nullable(),
  userId: OpaqueIdSchema.nullable(),
});

export const SupportTicketSummarySchema = z.strictObject({
  id: OpaqueIdSchema,
  number: z.number().int().min(1001),
  reference: z.string().min(2).max(20),
  organizationId: OpaqueIdSchema,
  customerName: z.string().min(1).max(160),
  customerSlug: z.string().min(1).max(63),
  subject: z.string().min(1).max(300),
  status: SupportTicketStatusSchema,
  priority: SupportTicketPrioritySchema,
  type: SupportTicketTypeSchema.nullable(),
  channel: SupportChannelSchema,
  toolId: OpaqueIdSchema.nullable(),
  requester: SupportRequesterSchema,
  assignee: SupportAssigneeSchema.nullable(),
  tags: z.array(z.string().min(1).max(40)).max(50),
  /** When the first reply was due, counted in business hours from the ticket's creation. */
  firstReplyDueAt: TimestampSchema.nullable(),
  /** While a customer waits for an answer: when the next reply is due. */
  replyDueAt: TimestampSchema.nullable(),
  firstRepliedAt: TimestampSchema.nullable(),
  lastCustomerMessageAt: TimestampSchema.nullable(),
  solvedAt: TimestampSchema.nullable(),
  hasDraft: z.boolean(),
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema,
});
export type SupportTicketSummary = z.infer<typeof SupportTicketSummarySchema>;

export const SupportAttachmentSchema = z.strictObject({
  id: OpaqueIdSchema,
  filename: z.string().min(1).max(255),
  mediaType: z.string().min(1).max(255),
  byteSize: z.number().int().nonnegative(),
});
export type SupportAttachment = z.infer<typeof SupportAttachmentSchema>;

export const SupportMessageSchema = z.strictObject({
  id: OpaqueIdSchema,
  kind: SupportMessageKindSchema,
  channel: SupportChannelSchema,
  authorKind: SupportActorKindSchema,
  authorName: z.string().min(1).max(160),
  authorEmail: z.string().max(320).nullable(),
  bodyText: z.string().min(1).max(100_000),
  autoSubmitted: z.boolean(),
  deliveryStatus: SupportDeliveryStatusSchema.nullable(),
  attachments: z.array(SupportAttachmentSchema).max(50),
  createdAt: TimestampSchema,
});
export type SupportMessage = z.infer<typeof SupportMessageSchema>;

export const SupportEventSchema = z.strictObject({
  id: OpaqueIdSchema,
  type: z.string().min(1).max(60),
  actorKind: SupportActorKindSchema,
  actorLabel: z.string().min(1).max(160),
  payload: z.record(z.string(), z.unknown()),
  createdAt: TimestampSchema,
});
export type SupportEvent = z.infer<typeof SupportEventSchema>;

export const SupportDraftSchema = z.strictObject({
  bodyText: z.string().min(1).max(100_000),
  sourceKind: z.enum(["employee", "workflow"]),
  sourceLabel: z.string().min(1).max(160),
  updatedAt: TimestampSchema,
});
export type SupportDraft = z.infer<typeof SupportDraftSchema>;

export const SupportParticipantSchema = z.strictObject({
  email: z.string().min(3).max(320),
  name: z.string().max(160),
  role: z.enum(["requester", "cc"]),
});

export const SupportTicketDetailSchema = SupportTicketSummarySchema.extend({
  deployedVersion: z.string().max(120).nullable(),
  participants: z.array(SupportParticipantSchema).max(100),
  messages: z.array(SupportMessageSchema).max(1000),
  events: z.array(SupportEventSchema).max(2000),
  draft: SupportDraftSchema.nullable(),
});
export type SupportTicketDetail = z.infer<typeof SupportTicketDetailSchema>;

export const SupportTicketListQuerySchema = z.strictObject({
  view: SupportViewKeySchema.default("open"),
  organizationId: OpaqueIdSchema.optional(),
  search: z.string().trim().max(200).optional(),
  cursor: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export type SupportTicketListQuery = z.infer<typeof SupportTicketListQuerySchema>;

export const SupportTicketListSchema = z.strictObject({
  tickets: z.array(SupportTicketSummarySchema).max(100),
  nextCursor: z.string().max(200).nullable(),
});

export const SupportViewCountsSchema = z.strictObject({
  views: z.array(z.strictObject({ key: SupportViewKeySchema, count: z.number().int() })),
  /** Pending unmatched mail, or `null` when this employee's support access is not global. */
  unmatched: z.number().int().nonnegative().nullable(),
});
export type SupportViewCounts = z.infer<typeof SupportViewCountsSchema>;

export const SupportTicketCreateSchema = z.strictObject({
  subject: z.string().trim().min(1).max(300),
  bodyText: BodySchema,
  requesterEmail: SupportEmailSchema.nullable().default(null),
  requesterName: NameSchema.default(""),
  priority: SupportTicketPrioritySchema.default("normal"),
  type: SupportTicketTypeSchema.nullable().default(null),
  toolId: OpaqueIdSchema.nullable().default(null),
});
export type SupportTicketCreate = z.infer<typeof SupportTicketCreateSchema>;

export const SupportTicketUpdateSchema = z
  .strictObject({
    status: SupportTicketStatusSchema.optional(),
    priority: SupportTicketPrioritySchema.optional(),
    type: SupportTicketTypeSchema.nullable().optional(),
    assigneeEmployeeId: OpaqueIdSchema.nullable().optional(),
    toolId: OpaqueIdSchema.nullable().optional(),
    addTags: z.array(SupportTagSchema).max(20).optional(),
    removeTags: z.array(SupportTagSchema).max(20).optional(),
  })
  .refine((value) => Object.keys(value).length > 0, "Change at least one field.");
export type SupportTicketUpdate = z.infer<typeof SupportTicketUpdateSchema>;

/** Raster images shown as thumbnails and inline in email; any other file is only downloaded. */
export const DISPLAYABLE_IMAGE_TYPES: ReadonlySet<string> = new Set([
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp",
]);

/** At most this many files on one reply or note. */
export const MAX_MESSAGE_ATTACHMENTS = 10;

/**
 * A person in the console may reply or add a note. Replies exist only there (ADR-0089). Files
 * are uploaded first and named here by their upload IDs.
 */
export const SupportMessageInputSchema = z.strictObject({
  kind: z.enum(["reply", "note"]),
  bodyText: BodySchema,
  nextStatus: SupportTicketStatusSchema.optional(),
  attachmentIds: z.array(OpaqueIdSchema).max(MAX_MESSAGE_ATTACHMENTS).optional(),
});
export type SupportMessageInput = z.infer<typeof SupportMessageInputSchema>;

/** A file uploaded for a reply or note that is not sent yet. */
export const SupportUploadSchema = z.strictObject({
  id: OpaqueIdSchema,
  filename: z.string().min(1).max(255),
  mediaType: z.string().min(1).max(255),
  byteSize: z.number().int().positive(),
});
export type SupportUpload = z.infer<typeof SupportUploadSchema>;
export const SupportUploadResultSchema = z.strictObject({ upload: SupportUploadSchema });

/** What the Operations MCP may add to a thread: internal notes only. */
export const SupportNoteInputSchema = z.strictObject({ bodyText: BodySchema });

export const SupportDraftInputSchema = z.strictObject({ bodyText: BodySchema });

export const SUPPORT_UNMATCHED_REASONS = [
  "unknown_sender",
  "ambiguous_sender",
  "automatic_reply",
  "invalid_reply_token",
  "unverified_sender",
] as const;
export const SupportUnmatchedReasonSchema = z.enum(SUPPORT_UNMATCHED_REASONS);

export const SupportUnmatchedMailSchema = z.strictObject({
  id: OpaqueIdSchema,
  receivedAt: TimestampSchema,
  envelopeFrom: z.string().min(1).max(320),
  fromAddress: z.string().max(320).nullable(),
  fromName: z.string().max(160),
  recipient: z.string().min(3).max(320),
  subject: z.string().max(998),
  bodyText: z.string().max(100_000),
  reason: SupportUnmatchedReasonSchema,
  authentication: z.string().max(500),
  suggestedOrganizationId: OpaqueIdSchema.nullable(),
  status: z.enum(["pending", "accepted", "rejected"]),
  ticketId: OpaqueIdSchema.nullable(),
  expiresAt: TimestampSchema,
});
export type SupportUnmatchedMail = z.infer<typeof SupportUnmatchedMailSchema>;

export const SupportUnmatchedListSchema = z.strictObject({
  mail: z.array(SupportUnmatchedMailSchema).max(200),
});

export const SupportUnmatchedAcceptSchema = z.strictObject({
  organizationId: OpaqueIdSchema,
  requesterName: NameSchema.optional(),
});

export const SupportTicketResponseSchema = z.strictObject({ ticket: SupportTicketDetailSchema });

/** The customer's tools a ticket can be linked to: its tool hubs and deployed tools (ADR-0086). */
export const SupportToolListSchema = z.strictObject({
  tools: z.array(z.strictObject({ id: OpaqueIdSchema, name: z.string().min(1).max(200) })).max(500),
});
export type SupportToolList = z.infer<typeof SupportToolListSchema>;

export const SupportAssigneeListSchema = z.strictObject({
  assignees: z.array(SupportAssigneeSchema).max(500),
});

export const SupportMessageResultSchema = z.strictObject({
  messageId: OpaqueIdSchema,
  status: SupportTicketStatusSchema,
  /** For a reply: whether it was emailed, failed, or stays in the workspace only. */
  delivery: z.enum(["sent", "failed", "not_sent"]).nullable(),
});

export const SupportUpdateResultSchema = z.strictObject({
  changed: z.array(z.string().min(1).max(40)).max(10),
  ticket: SupportTicketDetailSchema,
});

export const SupportUnmatchedAcceptResultSchema = z.strictObject({
  ticketId: OpaqueIdSchema,
  number: z.number().int().min(1001),
});
